'use client';

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { dispatchApiError } from '@/lib/providers/ApiErrorToastProvider';
import { extractApiErrorCode, extractApiErrorMessage } from '@/lib/utils/http';
import {
  addCommentToCache,
  type CommentsCacheSnapshot,
  restoreCommentsCaches,
  snapshotCommentsCaches,
  updateCommentInCache,
} from './cache';
import {
  type CreateCommentParams,
  createComment,
  deleteComment,
  dislikeComment,
  editComment,
  fetchCommentsPage,
  likeComment,
  removeCommentReaction,
} from './client';
import { commentsQueryKey, commentsQueryKeyPrefix } from './keys';
import type {
  CommentItem,
  CommentReaction,
  CommentSource,
  CommentsPage,
  CommentsSort,
} from './models';

export interface UseCommentsOptions {
  sort?: CommentsSort;
  onlyHolders?: boolean;
  onlyMine?: boolean;
  pageSize?: number;
  /** Seed the first page from SSR so the client does not refetch on mount. */
  initialPage?: CommentsPage;
}

export function useComments(source: CommentSource, options: UseCommentsOptions = {}) {
  const {
    sort = 'createdAt:desc',
    onlyHolders = false,
    onlyMine = false,
    pageSize,
    initialPage,
  } = options;
  const { isLoading: isAuthLoading, isAuthenticated } = useAuthContext();

  // `onlyMine` requires a JWT — the API answers 401 without one. The filter's own
  // checkbox is hidden for guests, but the flag itself survives a sign-out (or an
  // expired token), so drop it here rather than trusting the UI state.
  const effectiveOnlyMine = onlyMine && isAuthenticated;

  // Include auth state in the key so the list refetches once the user hydrates
  // or signs in/out — the API populates `isMine` / `myReaction` only with JWT.
  const queryKey = [
    ...commentsQueryKey(source, sort, onlyHolders, effectiveOnlyMine),
    isAuthenticated,
  ] as const;

  // Seed SSR data only for queries that match the SSR fetch shape (default sort,
  // no holders filter, no mine filter). Other shapes start empty so the queryKey
  // change naturally triggers a fetch.
  const canSeedSsr = initialPage && sort === 'createdAt:desc' && !onlyHolders && !effectiveOnlyMine;

  return useInfiniteQuery<CommentsPage, Error>({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchCommentsPage(source, {
        sort,
        onlyHolders,
        onlyMine: effectiveOnlyMine,
        page: pageParam as number,
        pageSize,
      }),
    // Seed SSR page synchronously and mark it fresh so a guest stays on the SSR
    // data without a refetch. For authed users, CommentsSection runs a one-shot
    // refetch() after auth hydrates to populate `isMine` / `userReaction`.
    initialData: canSeedSsr ? { pages: [initialPage], pageParams: [1] } : undefined,
    initialDataUpdatedAt: canSeedSsr ? Date.now() : undefined,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pageCount } = lastPage.pagination;
      return page < pageCount ? page + 1 : undefined;
    },
    // Wait for auth hydration so the initial fetch carries the JWT.
    enabled: !isAuthLoading,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

// CRUD mutations use onError for optimistic rollback, which makes the global
// MutationCache handler skip them — so we dispatch the error manually, same as reactions.
function handleCrudError(
  err: unknown,
  snapshot: CommentsCacheSnapshot | undefined,
  queryClient: ReturnType<typeof useQueryClient>,
) {
  if (snapshot) restoreCommentsCaches(queryClient, snapshot);
  dispatchApiError({
    code: extractApiErrorCode(err),
    message: extractApiErrorMessage(err) ?? 'An error occurred',
  });
}

export function useCreateComment(source: CommentSource) {
  const queryClient = useQueryClient();

  return useMutation<CommentItem, Error, CreateCommentParams>({
    mutationFn: (params) => createComment(source, params),
    onSuccess: (data, variables) => {
      addCommentToCache(queryClient, source, { ...data, isMine: true }, variables.parentCommentId);
    },
  });
}

export function useEditComment(source: CommentSource) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { commentId: string; content: string }, CommentsCacheSnapshot>({
    mutationFn: ({ commentId, content }) => editComment(commentId, content),
    onMutate: async ({ commentId, content }) => {
      await queryClient.cancelQueries({ queryKey: commentsQueryKeyPrefix(source) });
      const snapshot = snapshotCommentsCaches(queryClient, source);
      updateCommentInCache(queryClient, source, commentId, (c) => ({ ...c, content }));
      return snapshot;
    },
    onError: (err, _vars, snapshot) => handleCrudError(err, snapshot, queryClient),
  });
}

export function useDeleteComment(source: CommentSource) {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { commentId: string; deletedContent: string },
    CommentsCacheSnapshot
  >({
    mutationFn: ({ commentId }) => deleteComment(commentId),
    onMutate: async ({ commentId, deletedContent }) => {
      await queryClient.cancelQueries({ queryKey: commentsQueryKeyPrefix(source) });
      const snapshot = snapshotCommentsCaches(queryClient, source);
      updateCommentInCache(queryClient, source, commentId, (c) => ({
        ...c,
        isDeleted: true,
        content: deletedContent,
      }));
      return snapshot;
    },
    onError: (err, _vars, snapshot) => handleCrudError(err, snapshot, queryClient),
  });
}

type ReactionVars = { commentId: string; currentReaction: CommentReaction };

export function useCommentReactions(source: CommentSource) {
  const queryClient = useQueryClient();

  const like = useMutation<void, Error, ReactionVars, CommentsCacheSnapshot>({
    mutationFn: ({ commentId, currentReaction }) =>
      currentReaction === 'LIKE' ? removeCommentReaction(commentId) : likeComment(commentId),
    onMutate: async ({ commentId, currentReaction }) => {
      await queryClient.cancelQueries({ queryKey: commentsQueryKeyPrefix(source) });
      const snapshot = snapshotCommentsCaches(queryClient, source);
      updateCommentInCache(queryClient, source, commentId, (c) => {
        if (currentReaction === 'LIKE') {
          return { ...c, likesCount: c.likesCount - 1, userReaction: null };
        }
        return {
          ...c,
          likesCount: c.likesCount + 1,
          dislikesCount: currentReaction === 'DISLIKE' ? c.dislikesCount - 1 : c.dislikesCount,
          userReaction: 'LIKE',
        };
      });
      return snapshot;
    },
    onError: (err, _vars, snapshot) => handleCrudError(err, snapshot, queryClient),
  });

  const dislike = useMutation<void, Error, ReactionVars, CommentsCacheSnapshot>({
    mutationFn: ({ commentId, currentReaction }) =>
      currentReaction === 'DISLIKE' ? removeCommentReaction(commentId) : dislikeComment(commentId),
    onMutate: async ({ commentId, currentReaction }) => {
      await queryClient.cancelQueries({ queryKey: commentsQueryKeyPrefix(source) });
      const snapshot = snapshotCommentsCaches(queryClient, source);
      updateCommentInCache(queryClient, source, commentId, (c) => {
        if (currentReaction === 'DISLIKE') {
          return { ...c, dislikesCount: c.dislikesCount - 1, userReaction: null };
        }
        return {
          ...c,
          dislikesCount: c.dislikesCount + 1,
          likesCount: currentReaction === 'LIKE' ? c.likesCount - 1 : c.likesCount,
          userReaction: 'DISLIKE',
        };
      });
      return snapshot;
    },
    onError: (err, _vars, snapshot) => handleCrudError(err, snapshot, queryClient),
  });

  return { likeComment: like, dislikeComment: dislike };
}
